import React, { useEffect, useRef } from 'react';
import { X, Bot, Plus, Send, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import { useAIChat } from '@/hooks/useAIChat';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { AIMessage } from './AIMessage';
import { SuggestedActions } from './SuggestedActions';
import { ConfirmationCard } from './ConfirmationCard';
import s from './ai.module.css';

// ─── Typing Indicator ─────────────────────────────────────────────────────────

const TypingIndicator: React.FC = () => (
  <div className={s.typingRow}>
    <div className={s.botAvatarSm}>
      <Bot size={14} />
    </div>
    <div className={s.typingBubble}>
      <div className={s.typingDots}>
        <span className={s.typingDotGreen} style={{ animationDelay: '0ms' }} />
        <span className={s.typingDotGreen} style={{ animationDelay: '150ms' }} />
        <span className={s.typingDotGreen} style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ page?: string }> = ({ page }) => {
  const pageLabel = page
    ? page.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'your workspace';

  return (
    <div className={s.emptyState}>
      <div className={s.botIconMd}>
        <Bot size={20} />
      </div>
      <h3 className={s.emptyTitle}>CROS AI</h3>
      <p className={s.emptyBody}>
        I have full context on {pageLabel}. Ask me anything or pick an action below.
      </p>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

export const AISlidePanel: React.FC = () => {
  const location = useLocation();
  const { aiSlideOpen, setAISlideOpen, aiContext } = useUIStore();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    messages,
    sendMessage,
    isLoading,
    usage,
    error,
    clearConversation,
    resolveConfirmation,
  } = useAIChat();

  // Derive limit display from usage returned by the backend
  const messageCount = usage?.messages_today ?? 0;
  const messageLimit = usage ? usage.messages_today + usage.remaining : 0;
  const canSendMessage = usage ? usage.remaining > 0 : true;

  const [input, setInput] = React.useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentPage = location.pathname;

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (aiSlideOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [aiSlideOpen]);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && aiSlideOpen) setAISlideOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [aiSlideOpen, setAISlideOpen]);

  async function handleSend() {
    const text = input.trim();
    if (!text || isLoading || !canSendMessage) return;
    setInput('');
    await sendMessage(text);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleActionChip(message: string) {
    sendMessage(message);
  }

  if (!aiSlideOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className={s.slidePanelBackdrop}
          onClick={() => setAISlideOpen(false)}
        />
      )}

      {/* Slide panel */}
      <div className={s.slidePanel}>
        {/* Header — gradient bar matching signup panel */}
        <div className={s.slidePanelHeader}>
          <div className={s.slidePanelHeaderLeft}>
            <div className={s.slidePanelAvatar}>
              <Bot size={18} />
            </div>
            <div>
              <span className={s.slidePanelTitle}>CROS AI Manager</span>
              <div className={s.slidePanelStatus}>
                <span className={s.slidePanelStatusDot} />
                <span className={s.slidePanelStatusText}>Online</span>
                {messageLimit > 0 && (
                  <span className={s.slidePanelUsage}>
                    · {messageCount}/{messageLimit} today
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={s.slidePanelActions}>
            <button
              onClick={() => setAISlideOpen(false)}
              className={s.slidePanelCloseBtn}
              aria-label="Close AI panel"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Context tag */}
        {aiContext?.page && (
          <div className={s.slidePanelContext}>
            <span>
              Context: {aiContext.page.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          </div>
        )}

        {/* Messages */}
        <div className={s.messagesAreaLg}>
          {messages.length === 0 ? (
            <EmptyState page={currentPage} />
          ) : (
            messages.map((msg) => {
              // Render confirmation card if present
              if (msg.confirmation) {
                return (
                  <div key={msg.id} className={s.aiBubbleRow}>
                    <div className={s.botAvatarSm} style={{ marginTop: 2 }}>
                      <Bot size={14} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {msg.content && (
                        <div className={s.aiBubble}>
                          <p className={s.aiBubbleText}>{msg.content}</p>
                        </div>
                      )}
                      <ConfirmationCard
                        confirmation={msg.confirmation}
                        onResolved={(status, resultMessage) =>
                          resolveConfirmation(msg.id, status, resultMessage)
                        }
                      />
                    </div>
                  </div>
                );
              }

              return (
                <AIMessage
                  key={msg.id}
                  message={{ role: msg.role as 'user' | 'assistant', content: msg.content, timestamp: msg.timestamp }}
                />
              );
            })
          )}

          {isLoading && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Actions (shown when empty) */}
        {messages.length === 0 && (
          <div className={s.shrink0} style={{ padding: '0 18px 12px' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#555C72', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Suggested</p>
            <SuggestedActions page={currentPage} onAction={handleActionChip} />
          </div>
        )}

        {/* New conversation button — shown when there are messages */}
        {messages.length > 0 && (
          <div style={{ padding: '0 18px 8px', flexShrink: 0 }}>
            <button
              onClick={() => { clearConversation(); setInput(''); }}
              className={s.newConvoBtn}
            >
              <Plus size={14} />
              New conversation
            </button>
          </div>
        )}

        {/* Limit warning. Prefer the server-returned error message (tier-aware
            via buildDailyLimitMessage from R29-1) when the most recent send
            triggered the 429; fall back to a tier-neutral copy on page reload
            where usage was hydrated from /api/ai/usage with no recent error. */}
        {!canSendMessage && (
          <div className={s.limitBanner}>
            <p className={s.limitBannerText}>
              {error?.message || 'Daily message limit reached. Resets at midnight UTC.'}
            </p>
          </div>
        )}

        {/* Input */}
        <div className={s.inputWrap} style={{ padding: 16, borderColor: 'rgba(255,255,255,0.05)' }}>
          <div className={s.inputBoxGreen}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={canSendMessage ? 'Ask anything...' : 'Limit reached'}
              rows={1}
              disabled={isLoading || !canSendMessage}
              className={!canSendMessage ? s.textareaDisabled : s.textarea}
              style={{ overflowY: 'auto' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 128) + 'px';
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading || !canSendMessage}
              className={
                input.trim() && !isLoading && canSendMessage
                  ? s.sendBtnActiveGreen
                  : s.sendBtnDisabled
              }
              aria-label="Send"
            >
              {isLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
            </button>
          </div>
          <p className={s.inputHint}>
            Enter to send · Shift+Enter for new line
          </p>
        </div>
      </div>
    </>
  );
};
